import React, { useState, useRef, useEffect } from 'react';
import { Video, VideoOff, CheckCircle, XCircle, ArrowRight, Music } from 'lucide-react';

const CameraTest2 = () => {
  const [status, setStatus] = useState('idle'); // idle, requesting, success, error
  const [errorMsg, setErrorMsg] = useState('');
  const [stream, setStream] = useState(null);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [name, setName] = useState('');
  const [step, setStep] = useState('camera'); // camera, joined
  const videoRef = useRef(null);
  const sessionVideoRef = useRef(null);

  // Force video to play when stream changes
  useEffect(() => {
    if (stream && videoRef.current) {
      const video = videoRef.current;
      video.srcObject = stream;
      video.setAttribute('playsinline', 'true');
      video.setAttribute('webkit-playsinline', 'true');
      video.muted = true;
      
      const playVideo = async () => {
        try {
          await video.play();
          setVideoPlaying(true);
          console.log('Video now playing');
        } catch (err) {
          console.log('Play failed, will retry on tap:', err);
        }
      };
      
      playVideo();
      
      // Also try on loadedmetadata
      video.onloadedmetadata = () => {
        playVideo();
      };
    }
  }, [stream]);

  // Attach stream to session video when joining
  useEffect(() => {
    if (step === 'joined' && stream && sessionVideoRef.current) {
      sessionVideoRef.current.srcObject = stream;
      sessionVideoRef.current.play().catch(e => console.log('Session video play error:', e));
    }
  }, [step, stream]);

  const testCamera = async () => {
    setStatus('requesting');
    setErrorMsg('');
    setVideoPlaying(false);
    
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('getUserMedia not supported in this browser');
      }

      console.log('Requesting camera...');
      
      // First check what devices are available
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(d => d.kind === 'videoinput');
      const audioDevices = devices.filter(d => d.kind === 'audioinput');
      console.log('Available video devices:', videoDevices.length);
      console.log('Available audio devices:', audioDevices.length);
      
      if (videoDevices.length === 0) {
        throw new Error('No camera found on this device');
      }
      
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: true
      });

      console.log('Got stream:', mediaStream);
      setStream(mediaStream);
      setStatus('success');

    } catch (err) {
      console.error('Camera error:', err);
      setStatus('error');
      
      // More detailed error messages
      let errorMessage = '';
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage = 'Camera permission denied. Please check browser AND system permissions.';
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errorMessage = 'No camera found on this device.';
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        errorMessage = 'Camera is being used by another application. Close other apps using the camera.';
      } else if (err.name === 'OverconstrainedError') {
        errorMessage = 'Camera does not support the requested settings.';
      } else if (err.name === 'TypeError') {
        errorMessage = 'No camera or microphone found.';
      } else {
        errorMessage = `${err.name}: ${err.message}`;
      }
      
      setErrorMsg(errorMessage);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setStatus('idle');
    setVideoPlaying(false);
  };

  const forcePlay = async () => {
    if (videoRef.current && stream) {
      try {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setVideoPlaying(true);
      } catch (err) {
        console.error('Force play failed:', err);
      }
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center p-6">
      {step === 'camera' ? (
        <>
          <h1 className="text-3xl font-bold mb-2">Camera Test 2 + Join</h1>
          <p className="text-gray-400 mb-8">Enable camera, enter name, then join</p>

          {/* Video Preview */}
          <div 
            className="w-full max-w-md h-64 bg-gray-900 rounded-xl overflow-hidden mb-6 relative"
            onClick={forcePlay}
          >
            {stream ? (
              <>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                  style={{ transform: 'scaleX(-1)', backgroundColor: '#000' }}
                />
                {!videoPlaying && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/70">
                    <div className="text-center">
                      <Video className="w-12 h-12 mx-auto mb-2 text-amber-400" />
                      <p className="text-amber-400 font-bold">TAP HERE TO SEE VIDEO</p>
                    </div>
                  </div>
                )}
                <div className="absolute bottom-2 left-2 bg-green-500 text-black px-3 py-1 rounded-full text-sm font-bold flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  CAMERA WORKING!
                </div>
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                {status === 'requesting' ? (
                  <div className="text-center">
                    <div className="animate-spin w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                    <p>Requesting camera access...</p>
                  </div>
                ) : status === 'error' ? (
                  <div className="text-center text-red-400 p-4">
                    <XCircle className="w-16 h-16 mx-auto mb-4" />
                    <p className="font-bold text-xl mb-2">CAMERA FAILED</p>
                    <p className="text-sm bg-red-900/50 p-3 rounded">{errorMsg}</p>
                  </div>
                ) : (
                  <div className="text-center text-gray-500">
                    <VideoOff className="w-16 h-16 mx-auto mb-4" />
                    <p>Camera not started</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Name Input */}
          {stream && (
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your name"
              className="w-full max-w-md bg-gray-800 border-2 border-gray-600 rounded-xl px-4 py-3 text-lg text-center mb-4"
            />
          )}

          {/* Buttons */}
          <div className="flex gap-4">
            {!stream ? (
              <button
                onClick={testCamera}
                disabled={status === 'requesting'}
                className="bg-green-500 hover:bg-green-600 disabled:bg-gray-600 text-black font-bold px-8 py-4 rounded-xl text-xl"
              >
                TEST CAMERA
              </button>
            ) : (
              <button
                onClick={() => {
                  if (name.trim()) {
                    setStep('joined');
                  } else {
                    alert('Please enter your name');
                  }
                }}
                disabled={!name.trim()}
                className="bg-green-500 hover:bg-green-600 disabled:bg-gray-600 text-black font-bold px-8 py-4 rounded-xl text-xl flex items-center gap-2"
              >
                JOIN SESSION <ArrowRight className="w-5 h-5" />
              </button>
            )}
          </div>
        </>
      ) : (
        <>
          <h1 className="text-3xl font-bold mb-2">Session Joined!</h1>
          <p className="text-gray-400 mb-4">Welcome, {name}</p>
          
          {/* Session Video */}
          <div className="w-full max-w-md h-64 bg-gray-900 rounded-xl overflow-hidden mb-6 relative">
            <video
              ref={sessionVideoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
              style={{ transform: 'scaleX(-1)', backgroundColor: '#000' }}
            />
            <div className="absolute bottom-2 left-2 bg-green-500 text-black px-3 py-1 rounded-full text-sm font-bold">
              YOU - {name}
            </div>
          </div>
          
          <button
            onClick={() => setStep('camera')}
            className="bg-red-500 hover:bg-red-600 text-white font-bold px-8 py-4 rounded-xl text-xl"
          >
            LEAVE SESSION
          </button>
        </>
      )}
    </div>
  );
};

export default CameraTest2;
